import React, { useState } from 'react';
import {
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Text,
  Heading,
  Badge
} from '@chakra-ui/react';
import { useTickets } from '@hooks/useTickets';
import CustomerInfoPanel from './CustomerInfoPanel';
import CallHistoryList from './CallHistoryList';
import CallLogForm from './CallLogForm';
import TicketActionsPanel from './TicketActionsPanel';
import { getStatusColor } from '@views/OutboundCallingDashboard/utils';

/**
 * Ticket Modal Component
 * Comprehensive modal for ticket management
 */
const OutboundCallingTicketDetails = ({ isOpen, onClose, onTicketUpdate }) => {
  const { currentTicket, closeTicket } = useTickets();
  const [activeTab, setActiveTab] = useState(0);

  /**
   * Handle modal close
   */
  const handleClose = () => {
    closeTicket();
    setActiveTab(0);
    onClose();
  };

  if (!currentTicket) return null;

  return (
    <>
      <ModalHeader>
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between">
            <HStack spacing={3}>
              <Text fontSize="2xl" fontWeight="bold">
                {currentTicket.ticketId}
              </Text>
              {/* Ticket Status Badge */}
              {currentTicket.status && (
                <Badge
                  colorScheme={getStatusColor(currentTicket.status)}
                  fontSize="xs"
                  borderRadius={'xl'}
                  px={3}
                  py={1}
                >
                  {currentTicket.status}
                </Badge>
              )}
              {/*NOTE :: Hiding this for the demo */}
              {/*<Badge*/}
              {/*  colorScheme={getPriorityColor(currentTicket.priority)}*/}
              {/*  fontSize="xs"*/}
              {/*  borderRadius={'xl'}*/}
              {/*  px={2}*/}
              {/*>*/}
              {/*  {currentTicket.priority}*/}
              {/*</Badge>*/}
              {/*AUDIT :: Need to get the sla status*/}
              {/*<Badge*/}
              {/*  colorScheme={getSLAColor(currentTicket.slaStatus)}*/}
              {/*  fontSize="xs"*/}
              {/*  borderRadius={'xl'}*/}
              {/*  px={2}*/}
              {/*>*/}
              {/*  {currentTicket.slaStatus?.replace(/_/g, ' ')}*/}
              {/*</Badge>*/}
            </HStack>
          </HStack>

          {/* Ticket Note Section */}
          {currentTicket.agentRemarks && (
            <VStack align="stretch" spacing={1}>
              <Text
                fontSize="xs"
                color="gray.400"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Ticket Note
              </Text>
              <Text
                fontSize="md"
                color="whiteAlpha.900"
                fontWeight="medium"
                lineHeight="1.6"
              >
                {currentTicket.agentRemarks}
              </Text>
            </VStack>
          )}
        </VStack>
      </ModalHeader>
      <ModalCloseButton />

      <ModalBody>
        <VStack spacing={6} align="stretch">
          {/* Customer Information */}
          <CustomerInfoPanel ticket={currentTicket} />

          {/* Tabs for Call Management and Ticket Actions */}
          <Tabs
            variant="enclosed"
            size="sm"
            index={activeTab}
            onChange={setActiveTab}
          >
            <TabList color="whiteAlpha.700" borderColor="whiteAlpha.500">
              <Tab
                _selected={{
                  color: 'brand.500',
                  border: '1px solid',
                  borderColor: 'whiteAlpha.500',
                  borderBottom: '1px solid black'
                }}
                _focus={{
                  boxShadow: 'none'
                }}
              >
                Call Management
              </Tab>
              <Tab
                _selected={{
                  color: 'brand.500',
                  border: '1px solid',
                  borderColor: 'whiteAlpha.500',
                  borderBottom: '1px solid black'
                }}
                _focus={{
                  boxShadow: 'none'
                }}
              >
                Ticket Actions
              </Tab>
            </TabList>

            <TabPanels>
              {/* Call Management Tab */}
              <TabPanel px={0}>
                <VStack spacing={4} align="stretch">
                  {/* Call History */}
                  <Box
                    bg={'blackAlpha.500'}
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'stretch'}
                    gap={4}
                    p={4}
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                  >
                    <Heading fontSize="md" color="white">
                      Call History
                    </Heading>
                    <CallHistoryList ticket={currentTicket} />
                  </Box>

                  {/* Log New Call */}
                  <Box
                    bg={'blackAlpha.500'}
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'stretch'}
                    gap={4}
                    p={4}
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                  >
                    <Heading fontSize="md" color="white">
                      Log New Call
                    </Heading>
                    <CallLogForm
                      ticket={currentTicket}
                      onTicketUpdate={onTicketUpdate}
                    />
                  </Box>
                </VStack>
              </TabPanel>

              {/* Ticket Actions Tab */}
              <TabPanel px={0}>
                <TicketActionsPanel
                  ticket={currentTicket}
                  onTicketUpdate={onTicketUpdate}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </ModalBody>
    </>
  );
};

export default OutboundCallingTicketDetails;
